<script setup>
import { ref, onBeforeMount, computed, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import router from "@/router";

import AgentLayout from "@/views/layouts/AgentLayout.vue";
// import moment from "moment-timezone";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useAgentInstructionsStore } from "@/stores/agentInstructions";
import { useSearchStore } from "@/stores/search";
import { useThemeAssignmentsStore } from "@/stores/themeAssignments";
import { useShopifyStore } from "@/stores/shopify";
import {
  canonicalInstructionRevision,
  instructionActive,
  mappedIntegrationForSite,
  productRefs,
} from "@/utils/helpers/recommendationPlan";
const companyId = useCompanyStore().companyId;

const uiStore = useUiStore();
const agentInstructionsStore = useAgentInstructionsStore();
const searchStore = useSearchStore();
const themeAssignmentsStore = useThemeAssignmentsStore();
const shopifyStore = useShopifyStore();
const route = useRoute();

let id = ref(null);
let title = ref(null);
let prompt = ref(null);
let description = ref(null);
let type = ref("system_prompts");

let selectAllStores = ref(false);
let selectedStores = ref([]);
let storesOptions = ref([]);

let selectAllCategories = ref(false);
let selectedCategories = ref([]);
let categoriesOptions = ref([]);

let selectAllProducts = ref(false);
let selectedProducts = ref([]);
let productsOptions = ref([]);

let isSubmitted = ref(false);
let action = ref("add");
let btnText = ref("Submit");
let isStoresLoading = ref(false);
let isCategoriesLoading = ref(false);
let isProductsLoading = ref(false);
let startDateTime = ref(null);
let expiryDateTime = ref(null);
let recommendationEnabled = ref(false);
let recommendationTargets = ref([]);
let recommendationKind = ref("promoted");
let recommendationPreview = ref(null);
let recommendationContext = ref(null);
let recommendationError = ref(null);
let confirmRecommendationAmbiguities = ref(false);

// let currentDateTime = ref(new Date());

// const returnIdsVal = (arr) => {
//// };

// const storesSearchByName = (val) => {
//// fetchStoresByName(val);
// }
// };
// const productsSearchByName = (val) => {
////     fetchProductsByName(val);
//   }
// };
// const categoriesSearchByName = (val) => {
////     fetchCategoriesByName(val);
//   }
// };

const disabledDates = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  return (date) => {
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0); // Reset time to start of day
    return compareDate < today;
  };
});

const onSelectAllStoresChange = (event) => {
  selectedStores.value = event.checked
    ? storesOptions.value.map((item) => item.uuid)
    : [];

  selectAllStores.value = event.checked;
  if (selectedStores.value.length > 0) {
    fetchCategoriesByName();
    fetchProductsByName();
  }
};

const onChangeStoresVal = (event) => {
  selectedStores.value = event.value;
  if (selectedStores.value.length === 0) {
    selectedProducts.value = [];
    selectedCategories.value = [];
    productsOptions.value = [];
    categoriesOptions.value = [];
    selectAllProducts.value = false;
    selectAllCategories.value = false;
  } else if (selectedStores.value.length === storesOptions.value.length) {
    selectAllStores.value = true;
  } else if (selectedStores.value.length < storesOptions.value.length) {
    selectAllStores.value = false;
  }
  if (selectedStores.value.length) {
    fetchProductsByName();
    fetchCategoriesByName();
  }
};

const onChangeCategoriesVal = (event) => {
  selectedCategories.value = event.value;
  if (selectedCategories.value.length === categoriesOptions.value.length) {
    selectAllCategories.value = true;
  } else if (selectedCategories.value.length < categoriesOptions.value.length) {
    selectAllCategories.value = false;
  }
};

const onChangeProductsVal = (event) => {
  selectedProducts.value = event.value;
  if (selectedProducts.value.length === productsOptions.value.length) {
    selectAllProducts.value = true;
  } else if (selectedProducts.value.length < productsOptions.value.length) {
    selectAllProducts.value = false;
  }
};

const onSelectAllProductsChange = (event) => {
  selectedProducts.value = event.checked
    ? productsOptions.value.map((item) => item.uuid)
    : [];

  selectAllProducts.value = event.checked;
};

const fetchProductsByName = async () => {
  isProductsLoading.value = true;
  await searchStore
    .fetchProductsBySearch({
      companyId,
      params: { site_uuids: selectedStores.value, query: "" },
    })
    .then((res) => {
      isProductsLoading.value = false;
      productsOptions.value = res;
      selectedProducts.value = selectedProducts.value.filter((uuid) =>
        res.find((item) => item.uuid === uuid),
      );
    })
    .catch(() => {});
};

const onSelectAllCategoriesChange = (event) => {
  selectedCategories.value = event.checked
    ? categoriesOptions.value.map((item) => item.uuid)
    : [];

  selectAllCategories.value = event.checked;
};

const fetchCategoriesByName = async () => {
  isCategoriesLoading.value = true;
  await searchStore
    .fetchCategoriesBySearch({
      companyId,
      params: { site_uuids: selectedStores.value, query: "" },
    })
    .then((res) => {
      isCategoriesLoading.value = false;
      categoriesOptions.value = res;
      selectedCategories.value = selectedCategories.value.filter((uuid) =>
        res.find((item) => item.uuid === uuid),
      );
    })
    .catch(() => {});
};

const getStoreName = (uuid) => {
  const found = storesOptions.value.find((s) => s.uuid === uuid);
  return found ? found.name : uuid;
};

const removeStore = (uuid) => {
  selectedStores.value = selectedStores.value.filter((id) => id !== uuid);
  if (selectedStores.value.length) {
    fetchCategoriesByName();
    fetchProductsByName();
  }
};

const getCategoryName = (uuid) => {
  const found = categoriesOptions.value.find((s) => s.uuid === uuid);
  return found ? found.name : uuid;
};

const removeCategory = (uuid) => {
  selectedCategories.value = selectedCategories.value.filter(
    (id) => id !== uuid,
  );
};

const getProductName = (uuid) => {
  const found = productsOptions.value.find((s) => s.uuid === uuid);
  return found ? found.name : uuid;
};

const removeProduct = (uuid) => {
  selectedProducts.value = selectedProducts.value.filter((id) => id !== uuid);
};

const savedInstruction = (response) => response?.data || response || {};

const mappedRecommendationIntegration = async (siteId) => {
  if (!shopifyStore.integrations.length) await shopifyStore.fetchIntegrations(companyId);
  return mappedIntegrationForSite(shopifyStore.integrations, siteId);
};

const hydrateRecommendationStatus = async (saved) => {
  const siteId = Array.isArray(saved?.sites) && saved.sites.length === 1
    ? (saved.sites[0]?.uuid || saved.sites[0])
    : null;
  const instructionId = saved?.uuid || saved?.id;
  if (!siteId || !instructionId || saved?.type !== "categories_products") return;
  const integration = await mappedRecommendationIntegration(siteId);
  if (!integration) return;
  try {
    const response = await agentInstructionsStore.getRecommendationStatus({
      companyId, integrationId: integration.id, instructionId, siteId,
    });
    const state = response?.data || response;
    recommendationEnabled.value = true;
    recommendationKind.value = state.kind || "promoted";
    recommendationPreview.value = state;
    recommendationContext.value = {
      integrationId: integration.id, instructionId, siteId, revision: state.revision,
    };
  } catch (error) {
    const status = error?.status || error?.response?.status;
    if (status !== 404) recommendationError.value = "Recommendation status is unavailable.";
  }
};

const previewSavedRecommendation = async (saved) => {
  recommendationError.value = null;
  recommendationPreview.value = null;
  recommendationContext.value = null;
  const siteId = selectedStores.value.length === 1 ? selectedStores.value[0] : null;
  const instructionId = saved.uuid || saved.id || id.value;
  const instructionRevision = canonicalInstructionRevision(saved.updated_at);
  const sourceProducts = productRefs(selectedProducts.value);
  const targetProducts = productRefs(recommendationTargets.value);
  const integration = siteId ? await mappedRecommendationIntegration(siteId) : null;
  if (!siteId || !instructionId || !instructionRevision || !sourceProducts || !targetProducts || !integration) {
    throw new Error("Recommendation preview needs one mapped Site, one catalogue integration, and bounded source/target products.");
  }
  if (selectedProducts.value.some((uuid) => recommendationTargets.value.includes(uuid))) {
    throw new Error("A recommendation target cannot also be one of its trigger products.");
  }

  let expectedRevision = 0;
  try {
    const status = await agentInstructionsStore.getRecommendationStatus({
      companyId, integrationId: integration.id, instructionId, siteId,
    });
    expectedRevision = Number((status?.data || status)?.revision || 0);
  } catch (error) {
    const status = error?.status || error?.response?.status;
    if (status !== 404) throw error;
  }

  const response = await agentInstructionsStore.previewRecommendation({
    companyId,
    integrationId: integration.id,
    data: {
      site_id: siteId,
      instruction_id: instructionId,
      scoped_instruction_revision: instructionRevision,
      expected_revision: expectedRevision,
      revision: expectedRevision + 1,
      kind: recommendationKind.value,
      reason_code: "scoped_instruction",
      source_products: sourceProducts,
      target_products: targetProducts,
      confidence: null,
      active_from: saved.active_date || null,
      active_until: saved.expiry_date || null,
      active: instructionActive(saved.active_date, saved.expiry_date),
    },
  });
  recommendationPreview.value = response?.data || response;
  recommendationContext.value = {
    integrationId: integration.id, instructionId, siteId,
    revision: recommendationPreview.value.revision,
  };
  confirmRecommendationAmbiguities.value = false;
};

const activateRecommendation = async () => {
  if (!recommendationContext.value) return;
  isSubmitted.value = true;
  recommendationError.value = null;
  try {
    const context = recommendationContext.value;
    const response = await agentInstructionsStore.activateRecommendation({
      companyId,
      integrationId: context.integrationId,
      instructionId: context.instructionId,
      data: {
        site_id: context.siteId,
        expected_revision: context.revision,
        confirm_ambiguities: confirmRecommendationAmbiguities.value,
      },
    });
    recommendationPreview.value = {
      ...recommendationPreview.value,
      ...(response?.data || response),
    };
  } catch (error) {
    recommendationError.value = error?.data?.message || "Recommendation activation was refused.";
  } finally {
    isSubmitted.value = false;
  }
};

const refreshRecommendationStatus = async () => {
  if (!recommendationContext.value) return;
  recommendationError.value = null;
  try {
    const context = recommendationContext.value;
    const response = await agentInstructionsStore.getRecommendationStatus({
      companyId,
      integrationId: context.integrationId,
      instructionId: context.instructionId,
      siteId: context.siteId,
    });
    recommendationPreview.value = {
      ...recommendationPreview.value,
      ...(response?.data || response),
    };
  } catch (error) {
    recommendationError.value = error?.data?.message || "Recommendation status is unavailable.";
  }
};

const retryRecommendation = async () => {
  if (!recommendationContext.value) return;
  isSubmitted.value = true;
  recommendationError.value = null;
  try {
    const context = recommendationContext.value;
    const response = await agentInstructionsStore.retryRecommendation({
      companyId,
      integrationId: context.integrationId,
      instructionId: context.instructionId,
      data: { site_id: context.siteId, expected_revision: context.revision },
    });
    recommendationPreview.value = {
      ...recommendationPreview.value,
      ...(response?.data || response),
    };
  } catch (error) {
    recommendationError.value = error?.data?.message || "Recommendation retry was refused.";
  } finally {
    isSubmitted.value = false;
  }
};

const onSubmit = async () => {
  isSubmitted.value = true;

  const data = {
    id: id.value ? id.value : null,
    companyId: companyId,
    data: {
      title: title.value,
      prompt: prompt.value,
      description: description.value,
      type: type.value,
      sites: selectedStores.value,
      categories:
        selectedCategories.value && selectedCategories.value.length
          ? selectedCategories.value
          : null,
      products:
        selectedProducts.value && selectedProducts.value.length
          ? selectedProducts.value
          : null,
      active_date: startDateTime.value ? startDateTime.value : null,
      expiry_date: expiryDateTime.value ? expiryDateTime.value : null,
    },
  };

  try {
    const response = await agentInstructionsStore[
      action.value == "add" ? "addAgentInstructions" : "editAgentInstructions"
    ](data);
    const saved = savedInstruction(response);
    id.value = saved.uuid || saved.id || id.value;
    // The instruction is now persisted regardless of what happens below, so
    // any retry must PATCH it rather than re-run "add" and create a duplicate.
    action.value = "update";
    if (recommendationEnabled.value) {
      try {
        await previewSavedRecommendation(saved);
        btnText.value = "Update & preview";
      } catch (previewError) {
        recommendationError.value = previewError?.data?.message || previewError?.message || null;
      }
    } else {
      router.push("/agents-instructions");
    }
  } catch (error) {
    recommendationError.value = error?.data?.message || error?.message || null;
  } finally {
    isSubmitted.value = false;
  }
};

const fetchStoresByName = () => {
  isStoresLoading.value = true;
  storesOptions.value = [];
  themeAssignmentsStore
    .fetchStoresBySearch({ companyId, params: { query: "" } })
    .then((res) => {
      isStoresLoading.value = false;
      storesOptions.value = res;
    })
    .catch(() => {});
};

onBeforeMount(async () => {
  if (route.query.id) {
    action.value = "update";
    btnText.value = "Update";
    id.value = route.query.id;
    agentInstructionsStore
      .fetchAgentInstructionsById({ id: id.value, companyId })
      .then(async (res) => {
        title.value = res.title;
        prompt.value = res.prompt;
        description.value = res.description;
        type.value = res.type;
        selectedStores.value = res.sites.map((store) => store.uuid);
        await fetchCategoriesByName();
        await fetchProductsByName();

        setTimeout(() => {
          if (res.categories && res.categories.length) {
            selectedCategories.value = res.categories.map(
              (category) => category.uuid,
            );
          }
        }, 1000);

        setTimeout(() => {
          if (res.products && res.products.length) {
            selectedProducts.value = res.products.map(
              (product) => product.uuid,
            );
          }
        }, 1000);

        startDateTime.value = res.active_date ? res.active_date : null;
        expiryDateTime.value = res.expiry_date ? res.expiry_date : null;
        await hydrateRecommendationStatus(res);
      })
      .catch(() => {});
  }
});

const resetStoreData = () => {
  uiStore.setModalState(false);
  uiStore.setModalData({});
  uiStore.setModalAction(null);
};

onBeforeMount(() => {
  // fetchAllstores();
  fetchStoresByName();
  shopifyStore.fetchIntegrations(companyId).catch(() => {});
});

onUnmounted(() => {
  resetStoreData();
});
</script>

<template>
  <AgentLayout>
    <div class="settings-card card">
      <div class="mb-3">
        <DInput :value="title" @input="title = $event.target.value" label="Title" width="100%" />
      </div>

      <FloatLabel class="w-full mb-3">
        <PTextarea
          v-model="prompt"
          id="promptMessage"
          class="d-textarea resize-none h-105 w-full"
          maxlength="200"
          autoResize
        />
        <label for="promptMessage">Agent Instructions</label>
      </FloatLabel>

      <FloatLabel class="w-full mb-3">
        <PTextarea
          v-model="description"
          id="description"
          class="d-textarea resize-none h-105 w-full"
          maxlength="200"
          autoResize
        />
        <label for="description">Description</label>
      </FloatLabel>

      <!-- <div class="relative mb-3"> -->
      <!-- <span v-if="stores.length" class="multiselect-title-span">Sites</span>
        <VMultiselect
          :class="{ 'multiselect-options': stores.length }"
          v-model="stores"
          :options="storesOptions"
          :disabled="false"
          :multiple="true"
          :searchable="true"
          :show-labels="false"
          placeholder="Sites"
          trackBy="uuid"
          label="name"
          :loading="isStoresLoading"
          open-direction="bottom"
          :internal-search="false"
          :clear-on-select="false"
          :close-on-select="false"
          class="multiselect-relative"
          :show-no-results="true"
          :hide-selected="false"
          @search-change="storesSearchByName"
          selectLabel="Press enter to select"
          deselectLabel="Press enter to remove"
          :options-limit="10"

          
        >
          <template v-slot:noOptions>
            <span>Search by site name</span>
          </template>
        </VMultiselect> -->
      <!-- </div> -->

      <FloatLabel class="w-full md:w-80 mb-3" variant="in">
        <PMultiSelect
          v-model="selectedStores"
          :options="storesOptions"
          :selectAll="selectAllStores"
          optionLabel="name"
          optionValue="uuid"
          @selectall-change="onSelectAllStoresChange($event)"
          @change="onChangeStoresVal($event)"
          :virtualScrollerOptions="{ itemSize: 30 }"
          filter
          class="custom-multiselect"
          panelClass="custom-multiselect-panel"
          display="chip"
          id="sitesOptions"
          :maxSelectedLabels="2"
          :loading="isStoresLoading"
          filterPlaceholder="Search by site name"
        >
          <template #value="slotProps">
            <div
              v-if="slotProps.value && slotProps.value.length"
              class="flex items-center"
            >
              <div v-if="slotProps.value.length > 2">
                {{ slotProps.value.length }} items selected
              </div>
              <div v-else class="flex">
                <div
                  v-for="uuid in slotProps.value"
                  :key="uuid"
                  class="p-multiselect-token"
                >
                  <span class="p-multiselect-token-label">{{
                    getStoreName(uuid)
                  }}</span>
                  <span
                    class="p-multiselect-token-icon"
                    @click.stop="removeStore(uuid)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="p-icon"
                    >
                      <path
                        d="M10.5 3.5L3.5 10.5"
                        stroke="white"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M3.5 3.5L10.5 10.5"
                        stroke="white"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <span v-else>
              {{ slotProps.placeholder }}
            </span>
          </template>
        </PMultiSelect>
        <label for="sitesOptions">Sites</label>
      </FloatLabel>

      <div class="flex items-center justify-between mb-3">
        <div class="flex">
          <div class="relative">
            <span v-if="startDateTime" class="datepicker-title-span"
              >Start Date (Optional)</span
            >
            <VueDatePicker
              id="auto-brightness-time"
              auto-apply
              :clearable="startDateTime ? true : false"
              enable-seconds
              format="dd/MM/yyyy HH:mm:ss"
              v-model="startDateTime"
              :month-change-on-scroll="false"
              type="date"
              :disabled-dates="disabledDates"
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="Start Date (Optional)"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="startDateTime ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!startDateTime" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="startDateTime ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="startDateTime" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between mb-4">
        <div class="flex">
          <div class="relative">
            <span v-if="expiryDateTime" class="datepicker-title-span"
              >Expire Date (Optional)</span
            >
            <VueDatePicker
              id="auto-brightness-time"
              auto-apply
              :clearable="expiryDateTime ? true : false"
              enable-seconds
              format="dd/MM/yyyy HH:mm:ss"
              v-model="expiryDateTime"
              :month-change-on-scroll="false"
              type="date"
              :disabled-dates="disabledDates"
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="Expire Date (Optional)"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="expiryDateTime ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!expiryDateTime" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="expiryDateTime ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="expiryDateTime" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="unique-radio-inputs p-0 mb-4 radio-inputs-grid">
        <div class="radioOne p-0">
          <input
            @change="changeAppSourceVal"
            class="form-check-input"
            type="radio"
            name="type"
            id="systemPromptsOption"
            value="system_prompts"
            v-model="type"
          />
          <label class="form-check-label" for="systemPromptsOption"
            >System Prompts</label
          >
        </div>

        <div class="radioTwo p-0">
          <input
            @change="changeAppSourceVal"
            class="form-check-input"
            type="radio"
            name="type"
            id="categoriesProductsOption"
            value="categories_products"
            v-model="type"
          />
          <label class="form-check-label" for="categoriesProductsOption"
            >Categories & Products</label
          >
        </div>
      </div>

      <div v-if="type == 'system_prompts'">
        <div class="relative mb-3 w-416">
          <span class="hint-input ml-0"
            ><b>Note:</b> Use this Response Pattern for Requests You Cannot
            Fulfill:
            <b
              >"While I'm not able to [specific limitation], I can definitely
              [alternative solution]. [Explain alternatives or escalation path]
              Would that work for you, or is there another way I can help?"</b
            >
          </span>
        </div>
      </div>

      <div v-if="type == 'categories_products'" class="relative mb-3">
        <!-- <div class="relative mb-3">
          <span v-if="categories.length" class="multiselect-title-span"
            >Categories</span
          >
          <VMultiselect
            :class="{ 'multiselect-options': categories.length }"
            v-model="categories"
            :options="categoriesOptions"
            :disabled="!stores.length"
            :multiple="true"
            :searchable="true"
            :show-labels="false"
            placeholder="Categories"
            trackBy="uuid"
            label="name"
            :loading="isCategoriesLoading"
            open-direction="bottom"
            :internal-search="false"
            :clear-on-select="false"
            :close-on-select="false"
            class="multiselect-relative"
            :show-no-results="true"
            :hide-selected="false"
            @search-change="categoriesSearchByName"
          >
            <template v-slot:noOptions>
              <span>Search by category name</span>
            </template>
          </VMultiselect>
        </div> -->

        <FloatLabel class="w-full md:w-80 mb-3" variant="in">
          <PMultiSelect
            v-model="selectedCategories"
            :options="categoriesOptions"
            :selectAll="selectAllCategories"
            :disabled="!selectedStores.length"
            optionLabel="name"
            optionValue="uuid"
            @selectall-change="onSelectAllCategoriesChange($event)"
            @change="onChangeCategoriesVal($event)"
            :virtualScrollerOptions="{ itemSize: 30 }"
            filter
            class="custom-multiselect"
            panelClass="custom-multiselect-panel"
            display="chip"
            id="categoriesOptions"
            :maxSelectedLabels="2"
            :loading="isCategoriesLoading"
            filterPlaceholder="Search by category name"
          >
            <template #value="slotProps">
              <div
                v-if="slotProps.value && slotProps.value.length"
                class="flex items-center"
              >
                <div v-if="slotProps.value.length > 2">
                  {{ slotProps.value.length }} items selected
                </div>
                <div v-else class="flex">
                  <div
                    v-for="uuid in slotProps.value"
                    :key="uuid"
                    class="p-multiselect-token"
                  >
                    <span class="p-multiselect-token-label">{{
                      getCategoryName(uuid)
                    }}</span>
                    <span
                      class="p-multiselect-token-icon"
                      @click.stop="removeCategory(uuid)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="p-icon"
                      >
                        <path
                          d="M10.5 3.5L3.5 10.5"
                          stroke="white"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M3.5 3.5L10.5 10.5"
                          stroke="white"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
              <span v-else>
                {{ slotProps.placeholder }}
              </span>
            </template>
          </PMultiSelect>
          <label for="categoriesOptions">Categories</label>
        </FloatLabel>

        <!-- <div class="relative mb-3">
          <span v-if="products.length" class="multiselect-title-span"
            >Products</span
          >
          <VMultiselect
            :class="{ 'multiselect-options': products.length }"
            v-model="products"
            :options="productsOptions"
            :disabled="!stores.length"
            :multiple="true"
            :searchable="true"
            :show-labels="false"
            placeholder="Products"
            trackBy="uuid"
            label="name"
            :loading="isProductsLoading"
            open-direction="bottom"
            :internal-search="false"
            :clear-on-select="false"
            :close-on-select="false"
            class="multiselect-relative"
            :show-no-results="true"
            :hide-selected="false"
            @search-change="productsSearchByName"
          >
            <template v-slot:noOptions>
              <span>Search by product name</span>
            </template>
          </VMultiselect>
        </div> -->

        <FloatLabel class="w-full md:w-80 mb-3" variant="in">
          <PMultiSelect
            v-model="selectedProducts"
            :options="productsOptions"
            :disabled="!selectedStores.length"
            :selectAll="selectAllProducts"
            optionLabel="name"
            optionValue="uuid"
            @selectall-change="onSelectAllProductsChange($event)"
            @change="onChangeProductsVal($event)"
            :virtualScrollerOptions="{ itemSize: 30 }"
            filter
            class="custom-multiselect"
            panelClass="custom-multiselect-panel"
            display="chip"
            id="productsOptions"
            :maxSelectedLabels="2"
            :loading="isProductsLoading"
            filterPlaceholder="Search by product name"
          >
            <template #value="slotProps">
              <div
                v-if="slotProps.value && slotProps.value.length"
                class="flex items-center"
              >
                <div v-if="slotProps.value.length > 2">
                  {{ slotProps.value.length }} items selected
                </div>
                <div v-else class="flex">
                  <div
                    v-for="uuid in slotProps.value"
                    :key="uuid"
                    class="p-multiselect-token"
                  >
                    <span class="p-multiselect-token-label">{{
                      getProductName(uuid)
                    }}</span>
                    <span
                      class="p-multiselect-token-icon"
                      @click.stop="removeProduct(uuid)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="p-icon"
                      >
                        <path
                          d="M10.5 3.5L3.5 10.5"
                          stroke="white"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M3.5 3.5L10.5 10.5"
                          stroke="white"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
              <span v-else>
                {{ slotProps.placeholder }}
              </span>
            </template>
          </PMultiSelect>
          <label for="productsOptions">Products</label>
        </FloatLabel>

        <div class="recommendation-editor mb-3" data-test="recommendation-editor">
          <label class="flex items-center gap-2 mb-3">
            <input v-model="recommendationEnabled" type="checkbox" />
            <span>Use these products as a recommendation trigger</span>
          </label>
          <template v-if="recommendationEnabled">
            <p class="hint-input ml-0 mb-3">
              This keeps the Scoped Instruction as the single authoring surface.
              Select one mapped Site, trigger products above, and eligible local targets below.
            </p>
            <label class="block mb-2" for="recommendationKind">Relationship</label>
            <select id="recommendationKind" v-model="recommendationKind" class="form-control mb-3">
              <option value="promoted">Promoted</option>
              <option value="complementary">Complementary</option>
              <option value="related">Related</option>
              <option value="upsell">Upsell</option>
              <option value="alternative">Alternative</option>
              <option value="accessory">Accessory</option>
            </select>
            <FloatLabel class="w-full md:w-80 mb-3" variant="in">
              <PMultiSelect
                v-model="recommendationTargets"
                :options="productsOptions"
                :disabled="selectedStores.length !== 1"
                optionLabel="name"
                optionValue="uuid"
                :virtualScrollerOptions="{ itemSize: 30 }"
                filter
                class="custom-multiselect"
                panelClass="custom-multiselect-panel"
                display="chip"
                id="recommendationTargets"
                :maxSelectedLabels="2"
                :loading="isProductsLoading"
                filterPlaceholder="Search eligible target products"
              />
              <label for="recommendationTargets">Recommendation targets</label>
            </FloatLabel>

            <div v-if="recommendationPreview" class="recommendation-preview" data-test="recommendation-plan-preview">
              <strong>Interpreted recommendation</strong>
              <p>
                {{ recommendationPreview.trigger_count }} trigger(s) →
                {{ recommendationPreview.target_count }} target(s),
                {{ recommendationPreview.edge_count }} bounded edge(s).
              </p>
              <p>Status: {{ recommendationPreview.publication_state }}</p>
              <p v-if="recommendationPreview.ambiguities?.length">
                Needs confirmation: {{ recommendationPreview.ambiguities.join(", ") }}
              </p>
              <label v-if="recommendationPreview.publication_state === 'draft' && recommendationPreview.requires_confirmation" class="flex items-center gap-2 mb-3">
                <input v-model="confirmRecommendationAmbiguities" type="checkbox" />
                <span>Confirm this multi-product interpretation</span>
              </label>
              <button
                v-if="recommendationPreview.publication_state === 'draft'"
                type="button"
                class="btn btn-primary"
                :disabled="isSubmitted || (recommendationPreview.requires_confirmation && !confirmRecommendationAmbiguities)"
                @click="activateRecommendation"
              >
                Activate recommendation
              </button>
              <button
                v-else
                type="button"
                class="btn btn-secondary mr-2"
                :disabled="isSubmitted"
                @click="refreshRecommendationStatus"
              >
                Refresh publication status
              </button>
              <button
                v-if="recommendationPreview.retryable"
                type="button"
                class="btn btn-primary"
                :disabled="isSubmitted"
                @click="retryRecommendation"
              >
                Retry publication
              </button>
              <p v-if="recommendationPreview.next_safe_action">
                Next safe action: {{ recommendationPreview.next_safe_action.replaceAll("_", " ") }}
              </p>
            </div>
          </template>
          <p v-if="recommendationError" class="recommendation-error" role="alert">
            {{ recommendationError }}
          </p>
        </div>
      </div>

      <div class="flex mt-20 float-right right-bottom-btn">
        <button
          :disabled="
            !title ||
            !prompt ||
            !description ||
            !type ||
            !selectedStores.length ||
            isSubmitted ||
            (type == 'categories_products' &&
              !selectedCategories.length &&
              !selectedProducts.length) ||
            (recommendationEnabled &&
              (type !== 'categories_products' ||
                selectedStores.length !== 1 ||
                !selectedProducts.length ||
                !recommendationTargets.length))
          "
          @click="onSubmit"
          type="button"
          class="btn btn-primary add-button mr-30"
        >
          {{ btnText }}
          <span
            v-if="isSubmitted"
            class="spinner-sm"
            role="status"
            aria-hidden="true"
          ></span>
        </button>
      </div>
    </div>
  </AgentLayout>
</template>
<style scoped>
ul {
  padding-left: 0px !important;
}
.recommendation-editor {
  border: 1px solid #d9e2d7;
  border-radius: 8px;
  padding: 16px;
}
.recommendation-preview {
  background: #f4f8f3;
  border-left: 4px solid #2d8425;
  padding: 12px;
}
.recommendation-error {
  color: #b42318;
  margin-top: 12px;
}
</style>
